import {
  Button,
  SelectFieldController,
  Stack,
  TextInputFieldController,
} from '@fukuri-admins/ui/common';
import styles from './styles.module.scss';

type Props = {
  /** 都道府県の選択肢（value は 2 桁の都道府県コード） */
  prefectureOptions: React.ComponentProps<typeof SelectFieldController>['select']['options'];
  /** 保存 API 実行中かどうか（ボタンを無効化する） */
  isSaving: boolean;
  /**
   * キャンセルボタン押下時のコールバック
   * Button は `'button' | 'a'` のジェネリックなため `React.ComponentProps<typeof Button>['onClick']` だと
   * a 要素側のハンドラ型との union になり button に渡せない。渡し先の要素型（button）から取得する
   */
  onCancel: React.ComponentProps<'button'>['onClick'];
  /** フォーム送信時のコールバック（呼び出し側で handleSubmit を通したもの） */
  onSubmit: React.ComponentProps<'form'>['onSubmit'];
};

/**
 * 送付先住所編集フォームの Presentation
 *
 * 確認ダイアログの内側に表示する編集ビュー。フォームの状態は呼び出し側の FormProvider から受け取る。
 */
export const ShippingAddressEditFormPresentation: React.FC<Props> = ({
  prefectureOptions,
  isSaving,
  onCancel,
  onSubmit,
}) => {
  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <h2 className={styles.title} id='shipping-address-edit-form-title'>
        登録案内カードの発行内容の修正
      </h2>

      <Stack direction='column' gap={16} className={styles.content}>
        <div className={styles.employeeCountField}>
          <TextInputFieldController
            name='employeeCount'
            fieldWrapperProps={{ labelType: 'required', labelText: '利用人数' }}
            textInput={{ type: 'number', placeholder: '100' }}
          />
        </div>

        <Stack direction='column' gap={16}>
          <p className={styles.groupLabel}>送付先</p>
          <div className={styles.postalCodeField}>
            <TextInputFieldController
              name='postalCode'
              fieldWrapperProps={{ labelType: 'required', labelText: '郵便番号（ハイフン抜き）' }}
              textInput={{ placeholder: '0000000' }}
            />
          </div>
          <Stack direction='row' gap={24} alignItems='flex-start'>
            <div className={styles.prefectureField}>
              <SelectFieldController
                name='prefectureCode'
                fieldWrapperProps={{ labelType: 'required', labelText: '都道府県' }}
                select={{ placeholder: '都道府県を選択', options: prefectureOptions }}
              />
            </div>
            <div className={styles.cityField}>
              <TextInputFieldController
                name='city'
                fieldWrapperProps={{ labelType: 'required', labelText: '市区町村' }}
                textInput={{ placeholder: '〇〇市〇〇町' }}
              />
            </div>
          </Stack>
          <div className={styles.streetAddressField}>
            <TextInputFieldController
              name='streetAddress'
              fieldWrapperProps={{ labelType: 'required', labelText: '番地以降' }}
              textInput={{ placeholder: '1-2-3' }}
            />
          </div>
        </Stack>
      </Stack>

      <Stack direction='row' gap={8} alignItems='center' justifyContent='flex-end'>
        <Button type='button' variant='outline' onClick={onCancel} disabled={isSaving}>
          キャンセル
        </Button>
        <Button type='submit' variant='primary' disabled={isSaving}>
          保存
        </Button>
      </Stack>
    </form>
  );
};
